import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import DonateEvents from './DonateEvents';
import DonorsList from './DonorsList';

const DonatePage: React.FC = () => {
    const { t, i18n } = useTranslation();
    const isEnglish = i18n.language === 'en';

    return (
        <div className="bg-white mt-32">
            {/* 页面标题区域 */}
            <div className="bg-gradient-to-r from-blue-800 to-indigo-900 py-16 text-center">
                <motion.h1 
                    className="text-4xl font-bold text-white mb-4"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    {t('donate.title', isEnglish ? 'Your Support Matters' : '您的支持至关重要')}
                </motion.h1>
                <motion.p 
                    className="text-xl text-blue-100 max-w-3xl mx-auto px-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                >
                    {t('donate.subtitle')}
                </motion.p>
            </div>

            {/* 主要内容区域 */}
            <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
                {/* 捐赠介绍 */}
                <motion.section 
                    className="mb-16"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7 }}
                >
                    <h2 className="text-3xl font-bold text-gray-900 mb-6 border-b border-gray-200 pb-2">
                        {t('donate.introduction.title', isEnglish ? 'Why Your Support Matters' : '为什么您的支持很重要')}
                    </h2>
                    <div className="grid md:grid-cols-2 gap-8 items-center">
                        <div>
                            <p className="text-lg text-gray-700 mb-4">
                                {t('donate.introduction.paragraph1', isEnglish ? 
                                    'Great Lakes Academy is a nonprofit educational institution dedicated to providing students with high-quality educational resources and a supportive learning environment. Your generous donation directly contributes to:' : 
                                    '万湖书院是一所非营利性教育机构，致力于为学生提供高质量的教育资源和支持性的学习环境。您的慷慨捐赠将直接用于：'
                                )}
                            </p>
                            <ul className="list-disc pl-5 mb-4 space-y-2 text-gray-700">
                                <li>{t('donate.introduction.bullet1', isEnglish ? 'Enhancing school facilities' : '改善学校设施')}</li>
                                <li>{t('donate.introduction.bullet2', isEnglish ? 'Improving teaching quality' : '提升教学质量')}</li>
                                <li>{t('donate.introduction.bullet3', isEnglish ? 'Supporting student programs' : '支持学生项目')}</li>
                                <li>{t('donate.introduction.bullet4', isEnglish ? 'Promoting teacher development' : '促进教师发展')}</li>
                                <li>{t('donate.introduction.bullet5', isEnglish ? 'Celebrating cultural festivals' : '举办文化节日活动')}</li>
                            </ul>
                            <p className="text-lg text-gray-700 mb-4">
                                {t('donate.introduction.paragraph2', isEnglish ? 
                                    'As a school committed to preserving and promoting Chinese culture, we believe education has the power to change the world. With your support, we continue nurturing future leaders who possess a strong foundation in Chinese heritage and a global perspective.' : 
                                    '作为一所致力于保存和传承中华文化的学校，我们相信教育有改变世界的力量。在您的支持下，我们将继续培养具有深厚中华文化底蕴和国际视野的未来领袖。'
                                )}
                            </p>
                            <p className="text-lg text-gray-700">
                                {t('donate.introduction.paragraph3', isEnglish ? 
                                    'Every contribution, no matter the size, is a powerful expression of your commitment to education and the future. We sincerely thank you for supporting Great Lakes Academy.' : 
                                    '无论金额大小，每一笔捐赠都是您对教育和未来的有力支持。我们衷心感谢您对万湖书院的支持。'
                                )}
                            </p>
                        </div>
                        <div className="rounded-lg overflow-hidden shadow-xl">
                            <img 
                                src="/images/donate-main.jpg" 
                                alt={t('donate.introduction.imageAlt', isEnglish ? 'Supporting Education' : '支持教育')} 
                                className="w-full h-auto object-cover"
                                onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.src = '/images/default-bg.jpg';
                                }}
                            />
                        </div>
                    </div>
                </motion.section>

                {/* 捐助者列表 */}
                <DonorsList />

                {/* 筹款活动 */}
                <DonateEvents />

                {/* 捐赠方式 */}
                <motion.section 
                    className="mb-16"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7 }}
                >
                    <h2 className="text-3xl font-bold text-gray-900 mb-6 border-b border-gray-200 pb-2">
                        {t('donate.methods.title', isEnglish ? 'Ways to Donate' : '捐赠方式')}
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
                            <div className="flex items-center justify-center h-12 w-12 rounded-md bg-blue-600 text-white mb-4">
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-4">
                                {t('donate.methods.bank.title', isEnglish ? 'Bank Transfer' : '银行转账')}
                            </h3>
                            <p className="text-gray-700 mb-2">
                                {t('donate.methods.bank.accountName', isEnglish ? 'Organization Name: Great Lakes Academy' : '组织名称: 万湖书院')}
                            </p>
                            <p className="text-gray-700 mb-2">
                                {t('donate.methods.bank.taxId', isEnglish ? 'EIN: 33-4483075' : '税号: 33-4483075')}
                            </p>
                            <p className="text-gray-700 mb-2">
                                {t('donate.methods.bank.zelle', isEnglish ? 'Zelle: tuition@great-lakes-academy.org' : 'Zelle: tuition@great-lakes-academy.org')}
                            </p>
                            <p className="text-gray-700 mb-2">
                                {t('donate.methods.bank.bank', isEnglish ? 'Bank Name: Chase Bank' : '开户行: 大通银行 (Chase Bank)')}
                            </p>
                            <p className="text-gray-700">
                                {t('donate.methods.bank.note', isEnglish ? 'Note: Please indicate "Donation" and your name.' : '请在转账备注中注明"捐赠"和您的姓名。')}
                            </p>
                        </div>
                        
                        <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
                            <div className="flex items-center justify-center h-12 w-12 rounded-md bg-blue-600 text-white mb-4">
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-4">
                                {t('donate.methods.mail.title', isEnglish ? 'Mail a Check' : '邮寄支票')}
                            </h3>
                            <p className="text-gray-700 mb-2">
                                {t('donate.methods.mail.payableTo', isEnglish ? 'Payable To: Great Lakes Academy' : '收款人: 万湖书院')}
                            </p>
                            <p className="text-gray-700 mb-2">
                                {t('donate.methods.bank.taxId', isEnglish ? 'EIN: 33-4483075' : '税号: 33-4483075')}
                            </p>
                            <p className="text-gray-700 mb-2">
                                {t('donate.methods.mail.address', isEnglish ? 'Address: 3300 Plymouth Blvd., #46253, Plymouth, MN 55447' : '地址: 3300 Plymouth Blvd., #46253, Plymouth, MN 55447')}
                            </p>
                            <p className="text-gray-700">
                                {t('donate.methods.mail.note', isEnglish ? 'Note: Please indicate "Donation"' : '请注明"捐赠"')}
                            </p>
                        </div>
                    </div>
                </motion.section>

                {/* 联系我们 - 新增部分 */}
                {/* <motion.section 
                    className="mb-16"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7 }}
                >
                    <h2 className="text-3xl font-bold text-gray-900 mb-6 border-b border-gray-200 pb-2">
                        {t('donate.contact.title', isEnglish ? 'Contact Our Donation Office' : '联系我们的捐赠办公室')}
                    </h2>
                    <div className="bg-blue-50 p-6 rounded-lg">
                        <div className="flex items-center mb-4">
                            <svg className="h-6 w-6 text-blue-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                            <p className="text-gray-700">
                                {t('donate.contact.phone', isEnglish ? '📞 Phone: 1-800-123-4567' : '📞 电话: 1-800-123-4567')}
                            </p>
                        </div>
                        <div className="flex items-center">
                            <svg className="h-6 w-6 text-blue-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            <p className="text-gray-700">
                                {t('donate.contact.email', isEnglish ? '📧 Email: tuition@great-lakes-academy.org' : '📧 邮箱: tuition@great-lakes-academy.org')}
                            </p>
                        </div>
                    </div>
                </motion.section> */}

                {/* 常见问题 */}
                <motion.section 
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7 }}
                >
                    <h2 className="text-3xl font-bold text-gray-900 mb-6 border-b border-gray-200 pb-2">
                        {t('donate.faq.title', isEnglish ? 'Frequently Asked Questions' : '常见问题')}
                    </h2>
                    <div className="bg-white rounded-lg shadow-md divide-y divide-gray-200">
                        <div className="p-6">
                            <h3 className="text-xl font-semibold text-gray-900 mb-2">
                                {t('donate.faq.q1', isEnglish ? 'How will my donation be used?' : '我的捐赠将如何使用？')}
                            </h3>
                            <p className="text-gray-700">
                                {t('donate.faq.a1', isEnglish ? 
                                    'Your donation will be used for the purpose you specify. General donations will be allocated to areas of greatest need. We are committed to transparency and will publish regular reports on how donations are used.' : 
                                    '您的捐赠将用于您指定的目的。一般捐赠将分配到最需要的领域。我们致力于透明度，并将定期发布关于捐赠使用情况的报告。'
                                )}
                            </p>
                        </div>
                        
                        <div className="p-6">
                            <h3 className="text-xl font-semibold text-gray-900 mb-2">
                                {t('donate.faq.q2', isEnglish ? 'Can I get a donation receipt?' : '我可以获得捐赠收据吗？')}
                            </h3>
                            <p className="text-gray-700">
                                {t('donate.faq.a2', isEnglish ? 
                                    'Yes. We provide official donation receipts. Electronic receipts will be emailed. If you prefer a paper receipt, please request one at the time of donation or contact our office.' : 
                                    '是的。我们提供正式的捐赠收据。电子收据将通过电子邮件发送。如果您更喜欢纸质收据，请在捐赠时申请或联系我们的办公室。'
                                )}
                            </p>
                        </div>
                        
                        <div className="p-6">
                            <h3 className="text-xl font-semibold text-gray-900 mb-2">
                                {t('donate.faq.q3', isEnglish ? 'Is my donation tax-deductible?' : '我的捐赠是否可以抵税？')}
                            </h3>
                            <p className="text-gray-700">
                                {t('donate.faq.a3', isEnglish ? 
                                    'Great Lakes Academy is a nonprofit organization 501(c)(3) approval. Donations will be tax-deductible to the extent allowed by law. Please consult your tax advisor for details.' : 
                                    '万湖书院是一家501(c)(3)批准的非盈利组织。捐款可依法享受税收抵扣，具体减免额度请咨询您的税务顾问。'
                                )}
                            </p>
                        </div>
                        
                        <div className="p-6">
                            <h3 className="text-xl font-semibold text-gray-900 mb-2">
                                {t('donate.faq.q4', isEnglish ? 'Can I make recurring donations?' : '我可以进行定期捐赠吗？')}
                            </h3>
                            <p className="text-gray-700">
                                {t('donate.faq.a4', isEnglish ? 
                                    'Absolutely! Recurring donations help us plan for long-term projects. Contact our donation office to set up a recurring donation schedule.' : 
                                    '当然可以！定期捐赠帮助我们规划长期项目。请联系我们的捐赠办公室设置定期捐赠计划。'
                                )}
                            </p>
                        </div>
                    </div>
                </motion.section>
            </div>
        </div>
    );
};

export default DonatePage; 